/**
 * Ranking over the docs search index.
 *
 * Hand-written rather than MiniSearch/FlexSearch. The docs corpus is small
 * enough that a linear scan per keystroke is imperceptible, so a library
 * would buy fuzzy matching and cost a dependency plus its own index build
 * on every shard load. If typo tolerance turns out to matter, swapping the
 * internals of `searchDocs` is contained: the component only knows this
 * signature.
 *
 * What the scoring is tuned for, in order of how often it comes up:
 *
 *   1. Someone types a screen name they saw in the nav ("insights",
 *      "budget", "fetch health") and wants that page first, not the pages
 *      that mention it in passing. Hence title and heading outweigh body
 *      text by a wide margin.
 *   2. Someone pastes an identifier that contains underscores (permission
 *      codenames, env vars like `VITE_FRONTEND_PORT`, model/field names).
 *      Underscores are kept as word characters throughout so these stay
 *      single tokens rather than fragmenting into "vite", "frontend",
 *      "port".
 *   3. Someone types a partial word. Every term prefix-matches, so
 *      "fetc" finds "fetch" while an exact word match still scores higher.
 */

import { splitCategoryLabel } from '../docsCategory.js'

/**
 * Field weights. Ratios matter, absolute values don't. Title sits far
 * above body so that a page *about* fetch health beats the twenty pages
 * that mention it in passing.
 */
const WEIGHT = { title: 12, heading: 6, category: 3, text: 1 }

/** A term matching a whole word scores full; a prefix match scores less. */
const PREFIX_PENALTY = 0.4

/** Contiguous multi-word matches are almost always what was meant. */
const PHRASE_BONUS = 25

/** A page's own lead chunk (no heading) represents the page itself. */
const LEAD_CHUNK_BONUS = 2

const SNIPPET_RADIUS = 90
const MAX_SNIPPET_RANGES = 8

/**
 * Split into searchable terms. `\w` keeps underscores and digits, which is
 * what holds `VITE_FRONTEND_PORT` and `W1` together as single tokens.
 */
export function tokenize(input) {
    return input
        .toLowerCase()
        .split(/[^\w]+/)
        .filter(Boolean)
}

/**
 * Flattening every page into its chunks costs a few milliseconds, so it is
 * cached against the entries array identity — `loadDocsSearchIndex` returns
 * a stable array per permitted-audience set, so in practice this runs once
 * per session and every later keystroke reuses it.
 */
const preparedCache = new WeakMap()

function prepare(entries) {
    const cached = preparedCache.get(entries)
    if (cached) return cached

    const prepared = []
    for (const page of entries) {
        for (const chunk of page.chunks) {
            prepared.push({
                result: {
                    path: page.path,
                    title: page.title,
                    category: page.category,
                    heading: chunk.heading,
                    anchor: chunk.anchor,
                    audience: page.audience
                },
                text: chunk.text,
                lowerText: chunk.text.toLowerCase(),
                lowerTitle: page.title.toLowerCase(),
                lowerHeading: (chunk.heading ?? '').toLowerCase(),
                // The emoji-free text, not the raw label: a reader types
                // "meter data", never the plug emoji, so scoring against the
                // glyph would only ever add noise.
                lowerCategory: splitCategoryLabel(
                    page.category ?? ''
                ).text.toLowerCase(),
                isLead: chunk.depth <= 1
            })
        }
    }

    preparedCache.set(entries, prepared)
    return prepared
}

/**
 * Score one term against one field. Returns 0 when absent, so the caller
 * can require every term to appear somewhere.
 */
function scoreField(haystack, term, weight) {
    if (!haystack) return 0

    const at = haystack.indexOf(term)
    if (at === -1) return 0

    // Whole-word match if the character before and after aren't word
    // characters. `\w` again, so `vite_frontend` doesn't count as a
    // whole-word match inside `vite_frontend_port` — that's a prefix, and
    // should score as one.
    const before = at === 0 ? '' : haystack[at - 1]
    const afterIndex = at + term.length
    const after = afterIndex >= haystack.length ? '' : haystack[afterIndex]
    const isWholeWord = !/\w/.test(before) && !/\w/.test(after)

    return weight * (isWholeWord ? 1 : PREFIX_PENALTY)
}

/** Every occurrence of any term, merged into non-overlapping ranges. */
function highlightRanges(lowerHaystack, terms) {
    const ranges = []
    for (const term of terms) {
        let from = 0
        for (;;) {
            const at = lowerHaystack.indexOf(term, from)
            if (at === -1) break
            ranges.push([at, at + term.length])
            from = at + term.length
        }
    }

    ranges.sort((a, b) => a[0] - b[0])

    const merged = []
    for (const range of ranges) {
        const last = merged[merged.length - 1]
        if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
        else merged.push(range)
    }
    return merged.slice(0, MAX_SNIPPET_RANGES)
}

/** An excerpt centred on the first match, with ranges rebased onto it. */
function buildSnippet(chunk, terms) {
    const all = highlightRanges(chunk.lowerText, terms)
    const first = all[0]?.[0] ?? 0

    let start = Math.max(0, first - SNIPPET_RADIUS)
    let end = Math.min(chunk.text.length, first + SNIPPET_RADIUS * 2)

    // Don't cut mid-word at either edge.
    if (start > 0) {
        const space = chunk.text.indexOf(' ', start)
        if (space !== -1 && space < first) start = space + 1
    }
    if (end < chunk.text.length) {
        const space = chunk.text.lastIndexOf(' ', end)
        if (space > first) end = space
    }

    const text =
        (start > 0 ? '…' : '') +
        chunk.text.slice(start, end) +
        (end < chunk.text.length ? '…' : '')

    const offset = start - (start > 0 ? 1 : 0)
    const ranges = all
        .filter(([s, e]) => s >= start && e <= end)
        .map(([s, e]) => [s - offset, e - offset])

    return { text, ranges }
}

/**
 * Rank `entries` against `query`, best first.
 *
 * Every term must match somewhere in a chunk for it to be a candidate — an
 * AND, not an OR. For docs this is the right default: "fetch health"
 * should mean both words, and OR semantics on a corpus this size returns
 * most of it.
 */
export function searchDocs(entries, query, limit = 25) {
    const terms = tokenize(query)
    if (!terms.length) return []

    const phrase = query.trim().toLowerCase()
    const results = []

    for (const chunk of prepare(entries)) {
        let score = 0
        let matchedEveryTerm = true

        for (const term of terms) {
            const termScore =
                scoreField(chunk.lowerTitle, term, WEIGHT.title) +
                scoreField(chunk.lowerHeading, term, WEIGHT.heading) +
                scoreField(chunk.lowerCategory, term, WEIGHT.category) +
                scoreField(chunk.lowerText, term, WEIGHT.text)

            if (termScore === 0) {
                matchedEveryTerm = false
                break
            }
            score += termScore
        }

        if (!matchedEveryTerm) continue

        if (terms.length > 1) {
            if (chunk.lowerTitle.includes(phrase)) score += PHRASE_BONUS * 2
            else if (chunk.lowerHeading.includes(phrase)) score += PHRASE_BONUS
            else if (chunk.lowerText.includes(phrase)) score += PHRASE_BONUS / 2
        }

        if (chunk.isLead) score += LEAD_CHUNK_BONUS

        results.push({
            ...chunk.result,
            score,
            snippet: buildSnippet(chunk, terms)
        })
    }

    results.sort((a, b) => b.score - a.score || a.path.localeCompare(b.path))

    // At most one hit per heading. Without this a long page whose every
    // section mentions the query fills the entire result list and buries
    // every other page.
    const seen = new Set()
    const deduped = []
    for (const result of results) {
        const key = `${result.path}#${result.anchor ?? ''}`
        if (seen.has(key)) continue
        seen.add(key)
        deduped.push(result)
        if (deduped.length >= limit) break
    }

    return deduped
}
